/**
 * Alta, modificacion y borrado sobre el modelo.
 *
 * Lo que de verdad hace este modulo: mantener a raya la redundancia del
 * formato. Gramps guarda cada filiacion TRES veces --
 *
 *     <person><childof hlink="F"/>        la persona dice de quien es hija
 *     <person><parentin hlink="F"/>       y de quien es progenitora
 *     <family><childref hlink="P"/>       y la familia lista a sus hijos
 *
 * -- y si se descuadran, Gramps se comporta de forma impredecible. Por eso
 * NINGUNA parte de la aplicacion toca esos campos a mano: todo pasa por aqui,
 * y aqui se escriben siempre los tres a la vez.
 *
 * Las fechas se guardan como eventos, que es como las guarda el formato: una
 * fecha de nacimiento es un <event type="Birth"> con su <dateval> y su
 * <place>, referenciado desde la persona con un <eventref>.
 */
import {
  type Arbol,
  type Evento,
  type Familia,
  type Lugar,
  type Persona,
  type Sexo,
  type TipoUnion,
} from "./model.js";

export interface DatosPersona {
  nombres: string;
  apellido1: string;
  apellido2: string;
  /** Origen del primer apellido. Por convencion, del padre. */
  derivacion1: string;
  /** Origen del segundo. Por convencion, de la madre. */
  derivacion2: string;
  sexo: Sexo;
  /** "AAAA-MM-DD" o "AAAA". Vacio = no se sabe. */
  nacimiento?: string;
  lugarNacimiento?: string;
  fallecimiento?: string;
  lugarFallecimiento?: string;
}

export const DATOS_VACIOS: DatosPersona = {
  nombres: "",
  apellido1: "",
  apellido2: "",
  derivacion1: "Patrilineal",
  derivacion2: "Matrilineal",
  sexo: "U",
};

// ---------------------------------------------------------------------------
// Identificadores
// ---------------------------------------------------------------------------

function siguienteHandle(usados: Iterable<string>, prefijo: string): string {
  let max = 0;
  const re = new RegExp(`^${prefijo}(\\d+)$`);
  for (const h of usados) {
    const m = re.exec(h);
    if (m?.[1]) max = Math.max(max, Number(m[1]));
  }
  return `${prefijo}${String(max + 1).padStart(3, "0")}`;
}

function siguienteId(usados: Iterable<string | undefined>, prefijo: string): string {
  let max = 0;
  const re = new RegExp(`^${prefijo}(\\d+)$`);
  for (const id of usados) {
    if (!id) continue;
    const m = re.exec(id);
    if (m?.[1]) max = Math.max(max, Number(m[1]));
  }
  return `${prefijo}${String(max + 1).padStart(4, "0")}`;
}

// ---------------------------------------------------------------------------
// Lugares y eventos
// ---------------------------------------------------------------------------

/** Busca un lugar por nombre; si no existe lo crea. */
function lugarPorNombre(arbol: Arbol, nombre: string): string | undefined {
  const limpio = nombre.trim();
  if (!limpio) return undefined;
  for (const [h, l] of arbol.lugares) {
    if (l.nombres.some((n) => n.value.trim().toLowerCase() === limpio.toLowerCase())) {
      return h;
    }
  }
  const handle = siguienteHandle(arbol.lugares.keys(), "_l");
  const nuevo: Lugar = {
    handle,
    id: siguienteId(
      [...arbol.lugares.values()].map((x) => x.id),
      "P",
    ),
    change: "0",
    tipo: "City",
    nombres: [{ value: limpio, otrosAtribs: {} }],
    otrosAtribs: {},
    otros: {},
  };
  arbol.lugares.set(handle, nuevo);
  return handle;
}

/**
 * Pone (crea, actualiza o borra) el evento de un tipo para una persona.
 *
 * Si no hay ni fecha ni lugar, el evento se elimina: 58 de las 80 personas
 * del arbol real no tienen ninguno, y dejar eventos vacios por ahi solo
 * ensucia el fichero.
 */
function ponerEvento(
  arbol: Arbol,
  p: Persona,
  tipo: string,
  fecha: string | undefined,
  lugar: string | undefined,
): void {
  const valor = (fecha ?? "").trim();
  const hLugar = lugar ? lugarPorNombre(arbol, lugar) : undefined;

  let existente: Evento | undefined;
  for (const ref of p.eventos) {
    const e = arbol.eventos.get(ref.hlink);
    if (e?.tipo === tipo) {
      existente = e;
      break;
    }
  }

  if (!valor && !hLugar) {
    if (existente) {
      p.eventos = p.eventos.filter((r) => r.hlink !== existente?.handle);
      // Solo se borra el evento si no lo usa nadie mas.
      if (!algunaReferenciaA(arbol, existente.handle)) {
        arbol.eventos.delete(existente.handle);
      }
    }
    return;
  }

  if (existente) {
    existente.fecha = valor ? { etiqueta: "dateval", atribs: { val: valor } } : undefined;
    existente.lugar = hLugar;
    return;
  }

  const handle = siguienteHandle(arbol.eventos.keys(), "_e");
  const nuevo: Evento = {
    handle,
    id: siguienteId(
      [...arbol.eventos.values()].map((x) => x.id),
      "E",
    ),
    change: "0",
    tipo,
    fecha: valor ? { etiqueta: "dateval", atribs: { val: valor } } : undefined,
    lugar: hLugar,
    otrosAtribs: {},
    otros: {},
  };
  arbol.eventos.set(handle, nuevo);
  p.eventos.push({ hlink: handle, role: "Primary", otrosAtribs: {}, otros: {} });
}

function algunaReferenciaA(arbol: Arbol, handleEvento: string): boolean {
  for (const p of arbol.personas.values()) {
    if (p.eventos.some((r) => r.hlink === handleEvento)) return true;
  }
  for (const f of arbol.familias.values()) {
    if (f.eventos.some((r) => r.hlink === handleEvento)) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Personas
// ---------------------------------------------------------------------------

function aplicarDatos(arbol: Arbol, p: Persona, d: DatosPersona): void {
  p.sexo = d.sexo;
  const apellidos = [];
  if (d.apellido1.trim()) {
    apellidos.push({
      texto: d.apellido1.trim(),
      prim: true,
      derivation: d.derivacion1 || undefined,
      otrosAtribs: {},
    });
  }
  if (d.apellido2.trim()) {
    apellidos.push({
      texto: d.apellido2.trim(),
      prim: false,
      derivation: d.derivacion2 || undefined,
      otrosAtribs: {},
    });
  }

  const nombre = p.nombres[0];
  if (nombre) {
    nombre.first = d.nombres.trim() || undefined;
    nombre.apellidos = apellidos;
  } else {
    p.nombres.unshift({
      atribs: { type: "Birth" },
      first: d.nombres.trim() || undefined,
      apellidos,
      otros: {},
    });
  }

  ponerEvento(arbol, p, "Birth", d.nacimiento, d.lugarNacimiento);
  ponerEvento(arbol, p, "Death", d.fallecimiento, d.lugarFallecimiento);
}

export function crearPersona(arbol: Arbol, d: DatosPersona): string {
  const handle = siguienteHandle(arbol.personas.keys(), "_p");
  const p: Persona = {
    handle,
    id: siguienteId(
      [...arbol.personas.values()].map((x) => x.id),
      "I",
    ),
    change: "0",
    sexo: d.sexo,
    nombres: [],
    eventos: [],
    childof: [],
    parentin: [],
    otrosAtribs: {},
    otros: {},
  };
  arbol.personas.set(handle, p);
  aplicarDatos(arbol, p, d);
  return handle;
}

export function editarPersona(arbol: Arbol, handle: string, d: DatosPersona): void {
  const p = arbol.personas.get(handle);
  if (!p) throw new Error(`no existe la persona ${handle}`);
  aplicarDatos(arbol, p, d);
}

/** Los datos de una persona, para rellenar el formulario de edicion. */
export function datosDe(arbol: Arbol, handle: string): DatosPersona {
  const p = arbol.personas.get(handle);
  if (!p) return { ...DATOS_VACIOS };
  const n = p.nombres[0];
  const a1 = n?.apellidos.find((x) => x.prim) ?? n?.apellidos[0];
  const a2 = n?.apellidos.find((x) => x !== a1);

  const ev = (tipo: string): { fecha?: string; lugar?: string } => {
    for (const r of p.eventos) {
      const e = arbol.eventos.get(r.hlink);
      if (e?.tipo !== tipo) continue;
      const lugar = e.lugar ? arbol.lugares.get(e.lugar)?.nombres[0]?.value : undefined;
      return { fecha: e.fecha?.atribs["val"], lugar };
    }
    return {};
  };
  const nac = ev("Birth");
  const mue = ev("Death");

  return {
    nombres: n?.first ?? "",
    apellido1: a1?.texto ?? "",
    apellido2: a2?.texto ?? "",
    derivacion1: a1?.derivation ?? "Patrilineal",
    derivacion2: a2?.derivation ?? "Matrilineal",
    sexo: p.sexo,
    nacimiento: nac.fecha ?? "",
    lugarNacimiento: nac.lugar ?? "",
    fallecimiento: mue.fecha ?? "",
    lugarFallecimiento: mue.lugar ?? "",
  };
}

/**
 * Borra una persona y la saca de todas las familias.
 *
 * Una familia que se queda sin nadie se borra tambien: no aporta nada y el
 * validador la marcaria.
 */
export function borrarPersona(arbol: Arbol, handle: string): void {
  const p = arbol.personas.get(handle);
  if (!p) return;

  for (const fh of [...p.childof]) quitarHijo(arbol, fh, handle);
  for (const fh of [...p.parentin]) {
    const f = arbol.familias.get(fh);
    if (!f) continue;
    if (f.padre === handle) ponerProgenitor(arbol, fh, "padre", undefined);
    if (f.madre === handle) ponerProgenitor(arbol, fh, "madre", undefined);
  }

  for (const r of p.eventos) {
    const otros = algunaReferenciaAExcepto(arbol, r.hlink, handle);
    if (!otros) arbol.eventos.delete(r.hlink);
  }
  arbol.personas.delete(handle);

  for (const [fh, f] of [...arbol.familias]) {
    if (!f.padre && !f.madre && f.hijos.length === 0) arbol.familias.delete(fh);
  }
}

function algunaReferenciaAExcepto(
  arbol: Arbol,
  handleEvento: string,
  exceptoPersona: string,
): boolean {
  for (const [h, p] of arbol.personas) {
    if (h === exceptoPersona) continue;
    if (p.eventos.some((r) => r.hlink === handleEvento)) return true;
  }
  for (const f of arbol.familias.values()) {
    if (f.eventos.some((r) => r.hlink === handleEvento)) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Familias
// ---------------------------------------------------------------------------

export function crearFamilia(
  arbol: Arbol,
  padre: string | undefined,
  madre: string | undefined,
  rel: TipoUnion = "Unknown",
): string {
  const handle = siguienteHandle(arbol.familias.keys(), "_f");
  const f: Familia = {
    handle,
    id: siguienteId(
      [...arbol.familias.values()].map((x) => x.id),
      "F",
    ),
    change: "0",
    rel,
    eventos: [],
    hijos: [],
    otrosAtribs: {},
    otros: {},
  };
  arbol.familias.set(handle, f);
  if (padre) ponerProgenitor(arbol, handle, "padre", padre);
  if (madre) ponerProgenitor(arbol, handle, "madre", madre);
  return handle;
}

/**
 * Pone o quita un progenitor, manteniendo `parentin` al dia en las dos
 * personas afectadas: la que entra y la que sale.
 */
export function ponerProgenitor(
  arbol: Arbol,
  familia: string,
  rol: "padre" | "madre",
  persona: string | undefined,
): void {
  const f = arbol.familias.get(familia);
  if (!f) throw new Error(`no existe la familia ${familia}`);

  const anterior = rol === "padre" ? f.padre : f.madre;
  if (anterior === persona) return;

  if (anterior) {
    const p = arbol.personas.get(anterior);
    // Solo se le quita el parentin si no sigue siendo el otro progenitor.
    const sigue = rol === "padre" ? f.madre === anterior : f.padre === anterior;
    if (p && !sigue) p.parentin = p.parentin.filter((x) => x !== familia);
  }

  if (rol === "padre") f.padre = persona;
  else f.madre = persona;

  if (persona) {
    const p = arbol.personas.get(persona);
    if (!p) throw new Error(`no existe la persona ${persona}`);
    if (!p.parentin.includes(familia)) p.parentin.push(familia);
  }
}

export function ponerTipoUnion(arbol: Arbol, familia: string, rel: TipoUnion): void {
  const f = arbol.familias.get(familia);
  if (f) f.rel = rel;
}

/** Anade un hijo, escribiendo a la vez `childref` y `childof`. */
export function anadirHijo(arbol: Arbol, familia: string, persona: string): void {
  const f = arbol.familias.get(familia);
  const p = arbol.personas.get(persona);
  if (!f) throw new Error(`no existe la familia ${familia}`);
  if (!p) throw new Error(`no existe la persona ${persona}`);
  if (f.padre === persona || f.madre === persona) {
    throw new Error("una persona no puede ser hija y progenitora de la misma familia");
  }
  if (!f.hijos.some((r) => r.hlink === persona)) {
    f.hijos.push({ hlink: persona, otrosAtribs: {}, otros: {} });
  }
  if (!p.childof.includes(familia)) p.childof.push(familia);
}

export function quitarHijo(arbol: Arbol, familia: string, persona: string): void {
  const f = arbol.familias.get(familia);
  const p = arbol.personas.get(persona);
  if (f) f.hijos = f.hijos.filter((r) => r.hlink !== persona);
  if (p) p.childof = p.childof.filter((x) => x !== familia);
}

export function borrarFamilia(arbol: Arbol, familia: string): void {
  const f = arbol.familias.get(familia);
  if (!f) return;
  for (const r of [...f.hijos]) quitarHijo(arbol, familia, r.hlink);
  for (const h of [f.padre, f.madre]) {
    if (!h) continue;
    const p = arbol.personas.get(h);
    if (p) p.parentin = p.parentin.filter((x) => x !== familia);
  }
  arbol.familias.delete(familia);
}

// ---------------------------------------------------------------------------
// Atajos de alto nivel que usa la interfaz
// ---------------------------------------------------------------------------

/**
 * Busca la familia que une a dos personas como pareja, o la crea.
 * `madre` o `padre` pueden faltar: una familia con un solo progenitor es
 * valida y el arbol real tiene nueve.
 */
export function familiaDePareja(
  arbol: Arbol,
  padre: string | undefined,
  madre: string | undefined,
  rel: TipoUnion = "Unknown",
): string {
  for (const [h, f] of arbol.familias) {
    if (f.padre === padre && f.madre === madre) return h;
  }
  return crearFamilia(arbol, padre, madre, rel);
}

/** Asigna progenitores a alguien, creando la familia si hace falta. */
export function asignarProgenitores(
  arbol: Arbol,
  hijo: string,
  padre: string | undefined,
  madre: string | undefined,
  rel: TipoUnion = "Unknown",
): string {
  const p = arbol.personas.get(hijo);
  if (!p) throw new Error(`no existe la persona ${hijo}`);
  // Se saca de las familias en las que ya figuraba como hijo, para no dejarlo
  // registrado como hijo en dos sitios sin querer.
  for (const fh of [...p.childof]) quitarHijo(arbol, fh, hijo);
  const familia = familiaDePareja(arbol, padre, madre, rel);
  anadirHijo(arbol, familia, hijo);
  return familia;
}
